import React, { useState } from "react";
import { PageHeader } from "../components/layout/Shell";
import StatutoryShell from "../components/statutory/StatutoryShell";
import { StatutoryProvider } from "../components/statutory/StatutoryContext";
import { StatutoryBadge } from "../components/statutory/StatutoryBadge";
import { readinessMatrix, IMPL_AUTH_STATUS, SCHEMA_HEAD } from "../mock/statutory/mockData";
import { Info } from "lucide-react";

const COL_LABELS = [
  "Jurisdiction", "Regime", "Legal Source", "Interpretation",
  "Counsel", "Classifier", "Eligibility", "Calculation", "UI Readiness", "Activation",
];

const COL_KEYS = [
  "jurisdictionName", "regimeName", "legalSource", "interpretation",
  "counsel", "classifier", "eligibility", "calculationModel", "uiReadiness", "activationStatus",
];

const CELL_STYLE = {
  PRIMARY_SOURCE_VERIFIED: "bg-green-50 text-green-800",
  APPROVED_FOR_CONFIGURATION: "bg-green-50 text-green-800",
  DESIGN_READY: "bg-indigo-50 text-indigo-700",
  PROTOTYPE_READY: "bg-indigo-50 text-indigo-700",
  PENDING_ARB: "bg-blue-50 text-blue-800",
  SOURCE_IDENTIFIED: "bg-amber-50 text-amber-800",
  EVIDENCE_REQUIRED: "bg-orange-50 text-orange-800",
  LEGAL_INTERPRETATION_REQUIRED: "bg-amber-50 text-amber-800",
  COUNSEL_REVIEW_REQUIRED: "bg-red-50 text-red-800",
  BLOCKED: "bg-red-50 text-red-800",
  EXTERNALLY_BLOCKED: "bg-red-50 text-red-800",
  PARTIAL: "bg-yellow-50 text-yellow-800",
  NOT_STARTED: "bg-gray-50 text-gray-500",
  NOT_REQUIRED: "bg-gray-50 text-gray-400",
  OPEN: "bg-amber-50 text-amber-700",
  "N/A": "bg-gray-50 text-gray-400",
  default: "bg-background text-foreground",
};

const LEGEND = [
  { state: "DESIGN_READY", note: "Design artifact complete, awaiting implementation authorization" },
  { state: "PROTOTYPE_READY", note: "UI prototype complete, awaiting W11-L-IMPL authorization" },
  { state: "PENDING_ARB", note: "Artifact ready, awaiting ARB disposition" },
  { state: "SOURCE_IDENTIFIED", note: "Legal source located — not yet primary-source verified" },
  { state: "LEGAL_INTERPRETATION_REQUIRED", note: "Legal interpretation required before configuration" },
  { state: "COUNSEL_REVIEW_REQUIRED", note: "External counsel required — no calculation possible" },
  { state: "BLOCKED", note: "Configuration blocked pending legal verification or ARB" },
  { state: "NOT_STARTED", note: "No design or evidence work started" },
];

function CellValue({ value }) {
  if (!value || value === "NOT_REQUIRED" || value === "N/A") {
    return <span className="text-xs text-gray-400">—</span>;
  }
  const style = CELL_STYLE[value] || CELL_STYLE.default;
  // For text-only keys (jurisdiction name, regime name), just render text
  if (value.length > 20 && value.includes(" ")) {
    return <span className="text-xs text-foreground">{value}</span>;
  }
  return (
    <span className={`inline-flex items-center text-xs px-1.5 py-0.5 rounded font-medium ${style}`}>
      {value.replace(/_/g, " ")}
    </span>
  );
}

function MatrixContent() {
  const [filter, setFilter] = useState("ALL");
  const filters = ["ALL", "PENDING_ARB", "BLOCKED", "COUNSEL_REVIEW_REQUIRED"];

  const filtered = readinessMatrix.filter((row) => {
    if (filter === "ALL") return true;
    if (filter === "BLOCKED") return row.activationStatus === "BLOCKED";
    if (filter === "PENDING_ARB") return row.activationStatus === "PENDING_ARB";
    if (filter === "COUNSEL_REVIEW_REQUIRED") return row.counsel === "COUNSEL_REVIEW_REQUIRED";
    return true;
  });

  return (
    <div className="space-y-4">
      {/* Status summary */}
      <div className="grid grid-cols-3 gap-3 mb-2">
        <div className="rounded-lg border border-border bg-card p-3 text-center">
          <div className="text-xl font-bold text-foreground">{readinessMatrix.filter((r) => r.activationStatus === "PENDING_ARB").length}</div>
          <div className="text-xs text-muted-foreground mt-1">Pending ARB</div>
        </div>
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-center">
          <div className="text-xl font-bold text-red-700">{readinessMatrix.filter((r) => r.activationStatus === "BLOCKED").length}</div>
          <div className="text-xs text-red-600 mt-1">Blocked</div>
        </div>
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-center">
          <div className="text-2xl font-bold text-blue-600">0 / 6</div>
          <div className="text-xs text-blue-600 mt-1">ARB Gates Passed</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {filters.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`text-xs px-2.5 py-1 rounded border transition-colors ${filter === f ? "border-primary bg-primary/10 text-primary font-medium" : "border-border text-muted-foreground hover:text-foreground"}`}
            data-testid={`matrix-filter-${f}`}
          >
            {f.replace(/_/g, " ")}
          </button>
        ))}
      </div>

      {/* Matrix Table */}
      <div className="overflow-x-auto rounded-lg border border-border" data-testid="readiness-matrix-table">
        <table className="w-full text-xs min-w-[900px]">
          <thead className="bg-muted/60 sticky top-0 z-10">
            <tr>
              {COL_LABELS.map((h, i) => (
                <th key={h} className={`text-left px-3 py-3 font-semibold text-muted-foreground whitespace-nowrap border-b border-border ${i < 2 ? "sticky left-0 bg-muted/60 z-20" : ""}`}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.map((row, i) => {
              const isBlocked = row.activationStatus === "BLOCKED";
              return (
                <tr
                  key={i}
                  className={`transition-colors ${isBlocked ? "bg-red-50/20 hover:bg-red-50/40" : "hover:bg-muted/20"}`}
                  data-testid={`matrix-row-${row.regime}`}
                >
                  {COL_KEYS.map((key, j) => (
                    <td key={key} className={`px-3 py-3 ${j < 2 ? "font-medium" : ""}`}>
                      <CellValue value={row[key]} />
                      {key === "activationStatus" && row.blockingOQ && (
                        <div className="text-[10px] text-red-600 font-mono mt-0.5">→ {row.blockingOQ}</div>
                      )}
                      {key === "calculationModel" && row.note && (
                        <div className="text-[10px] text-amber-600 mt-0.5">{row.note}</div>
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ARB Evidence statement */}
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-xs text-red-800 font-semibold">
          W11-L-IMPL: {IMPL_AUTH_STATUS} · Schema head: {SCHEMA_HEAD}
        </p>
        <p className="text-xs text-red-700 mt-1">
          Implementation remains blocked pending ARB disposition. No activation, publishing, or production statutory configuration is authorized from this prototype.
        </p>
      </div>

      {/* Legend */}
      <div className="rounded-lg border border-border bg-card p-4">
        <div className="flex items-center gap-2 mb-3">
          <Info className="h-3.5 w-3.5 text-muted-foreground" />
          <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Legend</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {LEGEND.map(({ state, note }) => (
            <div key={state} className="flex items-center gap-2">
              <StatutoryBadge state={state} showIcon={false} />
              <span className="text-xs text-muted-foreground">{note}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function StatutoryReadinessMatrix() {
  return (
    <StatutoryProvider>
      <PageHeader
        eyebrow="Statutory Configuration"
        title="Statutory Readiness Matrix"
        description="Single operational view of jurisdiction × regime readiness. Designed for ARB and leadership review."
      />
      <StatutoryShell>
        <MatrixContent />
      </StatutoryShell>
    </StatutoryProvider>
  );
}
